import { useState, useEffect, useContext, useRef } from 'react';
import axios from 'axios';
import { AuthContext } from '../../contexts/AuthContext';
import './MyTasks.css';

const API_URL = process.env.REACT_APP_API_URL;

const toDateStr = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const formatDisplay = (dateStr) => {
  const [y, m, d] = dateStr.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  return date.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
};

const todayStr = () => toDateStr(new Date());

export default function MyTasks() {
  const { user } = useContext(AuthContext);
  const auth = () => ({ headers: { Authorization: `Bearer ${user?.token}` } });

  const [date, setDate]     = useState(todayStr());
  const [tasks, setTasks]   = useState([]);
  const [loading, setLoading] = useState(false);
  const [task, setTask]     = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]   = useState('');
  const textareaRef = useRef(null);

  const isToday = date === todayStr();

  const load = async (d) => {
    setLoading(true);
    setError('');
    try {
      const r = await axios.get(`${API_URL}/my-tasks?date=${d}`, auth());
      setTasks(r.data);
    } catch {
      setError('Failed to load tasks.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(date); }, [date]); // eslint-disable-line

  const prevDay = () => {
    const d = new Date(date + 'T00:00:00');
    d.setDate(d.getDate() - 1);
    setDate(toDateStr(d));
  };

  const nextDay = () => {
    const d = new Date(date + 'T00:00:00');
    d.setDate(d.getDate() + 1);
    if (toDateStr(d) <= todayStr()) setDate(toDateStr(d));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!task.trim()) return;
    setSubmitting(true);
    setError('');
    try {
      await axios.post(`${API_URL}/my-tasks`, { task }, auth());
      setTask('');
      load(date);
      textareaRef.current?.focus();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save task.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this task?')) return;
    try {
      await axios.delete(`${API_URL}/my-tasks/${id}`, auth());
      setTasks(prev => prev.filter(t => t.id !== id));
    } catch {
      setError('Failed to delete task.');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  return (
    <div className="mt-page">
      <h2 className="mt-title">My Tasks</h2>

      {isToday && (
        <form className="mt-form" onSubmit={handleSubmit}>
          <textarea
            ref={textareaRef}
            className="mt-textarea"
            placeholder="What did you work on? (Enter to submit, Shift+Enter for new line)"
            value={task}
            onChange={e => setTask(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={submitting}
            rows={3}
          />
          <button className="mt-btn mt-btn--primary" type="submit" disabled={submitting || !task.trim()}>
            {submitting ? 'Saving…' : 'Log Task'}
          </button>
        </form>
      )}

      {error && <p className="mt-error">{error}</p>}

      <div className="mt-table-section">
        <div className="mt-date-nav">
          <button className="mt-nav-btn" onClick={prevDay} title="Previous day">‹</button>
          <span className="mt-date-label">{formatDisplay(date)}{isToday ? ' (Today)' : ''}</span>
          <button className="mt-nav-btn" onClick={nextDay} disabled={isToday} title="Next day">›</button>
        </div>

        {loading ? (
          <p className="mt-loading">Loading…</p>
        ) : tasks.length === 0 ? (
          <p className="mt-empty">No tasks logged for this day.</p>
        ) : (
          <table className="mt-table">
            <thead>
              <tr>
                <th className="mt-col-time">Time</th>
                <th>Task</th>
                <th className="mt-col-action"></th>
              </tr>
            </thead>
            <tbody>
              {tasks.map(t => (
                <tr key={t.id}>
                  <td className="mt-col-time mt-time">{t.task_time}</td>
                  <td className="mt-task-text">{t.task}</td>
                  <td className="mt-col-action">
                    {isToday && (
                      <button className="mt-del-btn" onClick={() => handleDelete(t.id)} title="Remove">🗑️</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
